import React from 'react'
import { getLoadingMaskColors, LoadingMaskColorValue } from '.'
import { LoadingMaskCircleProps } from './loading-mask-circle'

interface LoadingMaskDonutProps extends LoadingMaskCircleProps {
  readonly innerCircleRadius: number
}

export const getSvgProps = ({
  id,
  frameSize,
  margin,
  radius,
  innerCircleRadius,
  colorValue
}: {
  readonly id: string | number
  readonly frameSize: number
  readonly margin: number
  readonly radius: number
  readonly innerCircleRadius: number
  readonly colorValue: LoadingMaskColorValue
}) => {
  const prefix = `loading-mask-donut-${id}`

  const ids = {
    mask: `${prefix}-mask`,
    gradient: `${prefix}-gradient`
  }

  // The full width and height of the square SVG
  const fullSize = frameSize + (margin === 0 ? 0 : margin * 2)
  const fullWidthProps = {
    width: fullSize,
    height: fullSize
  }

  // The center point of the full square SVG
  const center = fullSize / 2
  const centerProps = {
    cx: center,
    cy: center
  }

  const { baseColor: shimmerBaseColor, shimmerColor } =
    getLoadingMaskColors(colorValue)

  const commonAnimationProps = {
    begin: '0s',
    dur: '1s',
    repeatCount: 'indefinite'
  }

  const inc = 10
  const x1 = 0
  const x2 = inc

  const bgRectSize = frameSize

  return {
    svgProps: fullWidthProps,
    gradientProps: {
      id: ids.gradient,
      spreadMethod: 'repeat',
      x1,
      x2
    },
    gradientShapeProps: {
      fill: `url('#${ids.gradient}')`,
      className: `${prefix}-bg-rect`,
      width: bgRectSize,
      height: bgRectSize
    },
    visibleMaskProps: {
      className: `${prefix}-mask-visible`,
      ...centerProps,
      r: radius * 2,
      fill: 'white'
    },
    invisibleMaskProps: {
      className: `${prefix}-mask-invisible`,
      ...centerProps,
      r: radius,
      fill: 'black'
    },
    outerCircleProps: {
      className: `${prefix}-outer-mask`,
      ...centerProps,
      r: radius * 2,
      fill: 'white',
      mask: `url(#${ids.mask})`
    },
    innerCircleProps: {
      className: `${prefix}-inner-mask`,
      ...centerProps,
      r: innerCircleRadius,
      fill: 'white'
    },
    maskId: ids.mask,
    colorStopConfigs: [
      { offset: '0%', stopColor: shimmerBaseColor },
      { offset: '20%', stopColor: shimmerColor },
      { offset: '40%', stopColor: shimmerBaseColor },
      { offset: '100%', stopColor: shimmerBaseColor }
    ],
    animationConfigs: [
      {
        attributeName: 'x1',
        from: 0,
        to: inc
      },
      {
        attributeName: 'x2',
        from: inc,
        to: inc * 2
      }
    ].map((config) => ({
      ...commonAnimationProps,
      ...config
    }))
  }
}

export function LoadingMaskDonut({
  id,
  frameSize = 200,
  margin = 0,
  radius = 100,
  innerCircleRadius = 70,
  colorValue = 'normal'
}: LoadingMaskDonutProps) {
  const {
    svgProps,
    gradientProps,
    gradientShapeProps,
    visibleMaskProps,
    invisibleMaskProps,
    outerCircleProps,
    innerCircleProps,
    maskId,
    colorStopConfigs,
    animationConfigs
  } = getSvgProps({
    id,
    frameSize,
    margin,
    radius,
    innerCircleRadius,
    colorValue
  })

  return (
    <svg {...svgProps}>
      <defs>
        <linearGradient {...gradientProps}>
          {/* The color stops are what define the gradient */}
          {colorStopConfigs.map((props, index) => (
            <stop key={index} {...props} />
          ))}
          {/* The animate elements define the animation applied to the shape that has the gradient as a fill */}
          {animationConfigs.map((props, index) => (
            <animate key={index} {...props} />
          ))}
        </linearGradient>
      </defs>
      {/*
        This rectangle is the same size as the SVG.
        The animated gradient is applied to this.
        This can be though of as an animated background.
      */}
      <rect {...gradientShapeProps} />
      {/* These masks allow the animated gradient to appear as the fill color of the donut shape */}
      <mask id={maskId}>
        <circle {...visibleMaskProps} />
        <circle {...invisibleMaskProps} />
      </mask>
      {/* 
        This circle + the mask creates a hole that the donut will fit through. 
        It serves as an additional mask to block out the gradient outside the outer edge of the donut.
      */}
      <circle {...outerCircleProps} />
      {/* This circle will appear to be the hole in the center of the donut. */}
      {innerCircleRadius > 0 && <circle {...innerCircleProps} />}
    </svg>
  )
}
